import { createCloudRunAxios } from './axios';
import { updateOrganization } from './remiApi';

const remiApi = createCloudRunAxios('remi-api');

export interface OrganizationSetupRequest {
  organization_id: string;
  custom_email_domain?: string;
  email_domain?: string;
  reply_to_email?: string;
}

/**
 * Set up an organization for email campaigns
 */
export const setupOrganization = async (data: OrganizationSetupRequest) => {
  try {
    const response = await remiApi.post('/api/v1/setup-org', data);
    return response.data;
  } catch (error) {
    console.error('Error in setupOrganization:', error);
    throw error;
  }
};

/**
 * Activate email campaign functionality for an organization
 */
export const activateOrganizationCampaigns = async (organizationId: string) => {
  try {
    await updateOrganization(organizationId, {
      emailCampaignsEnabled: true,
      dnsConfigured: true,
    });
    return { success: true };
  } catch (error) {
    console.error('Error in activateOrganizationCampaigns:', error);
    throw error;
  }
};

/**
 * Deactivate email campaign functionality for an organization
 */
export const deactivateOrganizationCampaigns = async (organizationId: string) => {
  try {
    await updateOrganization(organizationId, {
      emailCampaignsEnabled: false,
    });
    return { success: true };
  } catch (error) {
    console.error('Error in deactivateOrganizationCampaigns:', error);
    throw error;
  }
};

/**
 * Authenticate organization's email domain
 */
export const authenticateDomain = async (organizationId: string) => {
  try {
    const response = await remiApi.post('/api/v1/authenticate-domain', {
      organization_id: organizationId,
    });
    return response.data;
  } catch (error) {
    console.error('Error in authenticateDomain:', error);
    throw error;
  }
};

/**
 * Update sender for organization's email campaigns
 */
export const updateSender = async (organizationId: string, reply_to_email: string) => {
  const response = await remiApi.post('/api/v1/update-sender', {
    organization_id: organizationId,
    reply_to_email,
  });
  return response.data;
};

/**
 * Schedule campaigns for an organization
 */
export const scheduleCampaign = async (organizationId: string) => {
  const response = await remiApi.post('/api/v1/schedule-campaign', {
    organization_id: organizationId,
  });
  return response.data;
};

export interface TestTemplateRequest {
  organization_id: string;
  template_id: string;
  test_email: string;
}

export const testTemplate = async (data: TestTemplateRequest) => {
  const response = await remiApi.post('/api/v1/email-campaign-function/test-template', data);
  return response.data;
};

export interface ContactsCampaignStatusRequest {
  organization_id: string;
  tags?: string[];
}

/**
 * Enable contacts for email campaigns by tags
 */
export const enableContactsByTags = async (data: ContactsCampaignStatusRequest) => {
  const response = await remiApi.post('/api/v1/enable-campaigns-by-tags', data);
  return response.data;
};

/**
 * Disable contacts for email campaigns by tags
 */
export const disableContactsByTags = async (data: ContactsCampaignStatusRequest) => {
  const response = await remiApi.post('/api/v1/disable-campaigns-by-tags', data);
  return response.data;
};

/**
 * Set up analytics for an organization
 */
export const setupAnalytics = async (organizationId: string) => {
  const response = await remiApi.post('/api/v1/analytics/setup', {
    organization_id: organizationId,
  });
  return response.data;
};
